import { Col, DatePicker, Form, Input, InputNumber, Modal, Radio, Row, Select, Spin, TimePicker } from 'antd'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
//
import { ALERT_TYPES, DATE_FORMAT, FORM_ITEM_LAYOUT, TIME_FORMAT } from 'util/constants'
import { showMessage } from 'util/helpers'

const FormModal = ({ visible, formName, width, handleCancel, handleOk, title, form, allfieldsData }) => {
  const [loading, setLoading] = React.useState(false)
  const [selectedType, setSelectedType] = useState(0)
  const [selectedActivity, setSelectedActivity] = useState('')
  const [daySections, setDaySections] = useState([])
  const [showAddDateButton, setShowAddDateButton] = useState(false)
  const [addedDays, setAddedDays] = useState(0)
  const [canAddMore, setCanAddMore] = useState(false)
  const [forceUpdate, setForceUpdate] = useState(0)

  useEffect(() => {
    const typeId = form.getFieldValue('type_id')
    setSelectedType(typeId ?? 0)
  }, [form])

  // Sync selectedActivity from purpose id so Description shows in edit mode when Purpose is "Other"
  useEffect(() => {
    if (!visible) return
    const purposeId = form.getFieldValue('tour_purpose_id')
    if (!purposeId) return
    const purposeName = allfieldsData?.purpose?.find((p) => p.id === purposeId)?.name
    if (purposeName) setSelectedActivity(purposeName)
  }, [visible, form, allfieldsData])

  useEffect(() => {
    if (!visible) return
    const currentType = form.getFieldValue('type_id')
    if (currentType !== 3) return
    const fromVal = form.getFieldValue('from_date')
    const toVal = form.getFieldValue('to_date')
    if (fromVal && toVal) {
      const sections = generateDaySections(fromVal, toVal)
      setDaySections(sections)
      setShowAddDateButton(true)
      setCanAddMore(true)
      const allValues = form.getFieldsValue()
      const filledPrefixes = new Set()
      Object.keys(allValues || {}).forEach((key) => {
        const match = key && key.match(/^day_(\d+)_([0-9-]+)_(location|planned_activity|transport|description)$/)
        if (match) {
          const [, dIndex, dDate] = match
          filledPrefixes.add(`day_${dIndex}_${dDate}`)
        }
      })
      const presetCount = filledPrefixes.size
      if (presetCount > 0) {
        setAddedDays(sections.length)
        setCanAddMore(false)
      } else {
        setAddedDays(sections.length > 0 ? 1 : 0)
        setCanAddMore(true)
      }
    }
  }, [visible, form, selectedType])

  const calculateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 0
    const start = moment.isMoment(startDate) ? startDate.clone() : moment(startDate, DATE_FORMAT)
    const end = moment.isMoment(endDate) ? moment(endDate) : moment(endDate, DATE_FORMAT)
    return end.diff(start, 'days') + 1 // +1 to include both start and end dates
  }

  const generateDaySections = (startDate, endDate) => {
    if (!startDate || !endDate) return []

    const days = []
    const start = moment.isMoment(startDate) ? startDate.clone() : moment(startDate, DATE_FORMAT)
    const end = moment.isMoment(endDate) ? moment(endDate) : moment(endDate, DATE_FORMAT)
    let dayCount = 1

    while (start.isSameOrBefore(end)) {
      days.push({
        day: `Day ${dayCount}`,
        date: start.format('DD-MM-YYYY'),
        fullDate: start.format('YYYY-MM-DD'),
        key: `day_${dayCount}_${start.format('YYYY-MM-DD')}`
      })
      start.add(1, 'day')
      dayCount++
    }

    return days
  }

  const handleFromDateChange = (date) => {
    const toDate = form.getFieldValue('to_date')
    if (date && toDate) {
      const dateRange = calculateDateRange(date, toDate)
      const sections = generateDaySections(date, toDate)
      setDaySections(sections)

      if (dateRange === 1) {
        // Single day - show trip planning but no "Add Plan" button needed
        setShowAddDateButton(false)
        setCanAddMore(false)
        setAddedDays(1)
      } else if (dateRange > 1) {
        // Multiple days - show "Add Plan" button
        setShowAddDateButton(true)
        setCanAddMore(true)
        setAddedDays(1)
      } else {
        // Invalid date range
        setShowAddDateButton(false)
        setCanAddMore(false)
        setAddedDays(0)
      }
    } else {
      setDaySections([])
      setShowAddDateButton(false)
      setCanAddMore(false)
      setAddedDays(0)
    }
  }

  const handleToDateChange = (date) => {
    const fromDate = form.getFieldValue('from_date')
    if (fromDate && date) {
      const dateRange = calculateDateRange(fromDate, date)
      const sections = generateDaySections(fromDate, date)
      setDaySections(sections)

      if (dateRange === 1) {
        // Single day - show trip planning but no "Add Plan" button needed
        setShowAddDateButton(false)
        setCanAddMore(false)
        setAddedDays(1)
      } else if (dateRange > 1) {
        // Multiple days - show "Add Plan" button
        setShowAddDateButton(true)
        setCanAddMore(true)
        setAddedDays(1)
      } else {
        // Invalid date range
        setShowAddDateButton(false)
        setCanAddMore(false)
        setAddedDays(0)
      }
    } else {
      setDaySections([])
      setShowAddDateButton(false)
      setCanAddMore(false)
      setAddedDays(0)
    }
  }

  const addNewDay = () => {
    if (canAddMore && addedDays < daySections.length) {
      setAddedDays(addedDays + 1)
      if (addedDays + 1 >= daySections.length) {
        setCanAddMore(false)
      }
      setForceUpdate(prev => prev + 1) // Force re-render to update submit button
    }
  }

  const removeDay = (index) => {
    if (addedDays > 0) {
      setAddedDays(addedDays - 1)
      setCanAddMore(true)
      const dayToRemove = daySections[index]
      if (dayToRemove) {
        form.setFieldsValue({
          [`${dayToRemove.key}_date`]: undefined,
          [`${dayToRemove.key}_location`]: undefined,
          [`${dayToRemove.key}_planned_activity`]: undefined,
          [`${dayToRemove.key}_transport`]: undefined,
          [`${dayToRemove.key}_description`]: undefined,
        })
      }
      setForceUpdate(prev => prev + 1) // Force re-render to update submit button
    }
  }

  const isFormValid = () => {
    // Check if purpose is selected
    const purpose = form.getFieldValue('tour_purpose_id')
    if (!purpose) return false

    // Check if description is required and provided when purpose is "Other"
    if (selectedActivity === 'Other') {
      const description = form.getFieldValue('description')
      if (!description || description.trim() === '') return false
    }

    // Check day sections if type is 3 (trip)
    if (selectedType === 3) {
      const fromDate = form.getFieldValue('from_date')
      const toDate = form.getFieldValue('to_date')

      if (!fromDate || !toDate) return false

      const dateRange = calculateDateRange(fromDate, toDate)

      // For single day, no day sections validation needed
      if (dateRange === 1) return true

      // For multiple days, check if all day sections are properly filled
      if (addedDays === 0) return false

      // Check all added days are properly filled (not requiring all possible days to be filled)
      for (let i = 0; i < addedDays; i++) {
        const day = daySections[i]
        const location = form.getFieldValue(`${day.key}_location`)
        const plannedActivity = form.getFieldValue(`${day.key}_planned_activity`)
        const transport = form.getFieldValue(`${day.key}_transport`)
        const description = form.getFieldValue(`${day.key}_description`)

        // Check if any field is empty or undefined
        if (!location || !location.trim() ||
          !plannedActivity || (Array.isArray(plannedActivity) && plannedActivity.length === 0) ||
          !transport ||
          !description || !description.trim()) {
          return false
        }
      }
    }
    return true
  }

  const onValuesChange = (values) => {
    Object.keys(values).forEach((field) => {
      const error = form.getFieldError(field)
      if (!error.length) {
        return
      }
      form.setFields([
        {
          name: field,
          errors: [],
        },
      ])
    })

    if (Object.prototype.hasOwnProperty.call(values, 'from_date') || Object.prototype.hasOwnProperty.call(values, 'to_date')) {
      const fromVal = form.getFieldValue('from_date')
      const toVal = form.getFieldValue('to_date')
      if (fromVal && toVal) {
        const dateRange = calculateDateRange(fromVal, toVal)
        const sections = generateDaySections(fromVal, toVal)
        setDaySections(sections)

        if (dateRange <= 1) {
          setShowAddDateButton(false)
          setCanAddMore(false)
          setAddedDays(0)
        } else {
          setShowAddDateButton(true)
          setCanAddMore(true)
          setAddedDays(1)
        }
      } else {
        setDaySections([])
        setShowAddDateButton(false)
        setCanAddMore(false)
        setAddedDays(0)
      }
    }
  }

  const handleSubmit = async (value) => {
    setLoading(true)
    try {
      await handleOk(value)
      form.resetFields()
      handleCancel()
      // history.push(`/kpi/${res?.id}`)
    } catch (error) {
      try {
        console.error('PRINT IN %s=====>', 'Form Submit First', error)
        const eRes = await error.response.json().then((res) => res)
        showMessage(ALERT_TYPES.ERROR, eRes.message)
        const errors = eRes.errors.children
        const data = []
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const v in value) {
          const i = {
            name: v,
            ...errors[v],
          }
          data.push(i)
        }
        form.setFields(data)
      } catch (error2) {
        console.error('PRINT IN %s=====>', 'Form Submit', error2)
        showMessage(ALERT_TYPES.WARN, 'Oops, Something Wrong!')
      }
    }
    setLoading(false)
  }

  // const isSubmitDisabled = () => {
  //   if (selectedType !== 3) return false

  //   const fromDate = form.getFieldValue('from_date')
  //   const toDate = form.getFieldValue('to_date')

  //   if (!fromDate || !toDate) return true

  //   const dateRange = calculateDateRange(fromDate, toDate)

  //   // If date range is 1 day, submit should not be disabled (single day planning)
  //   if (dateRange === 1) return false

  //   // If date range is more than 1 day, check if all day sections are filled
  //   if (dateRange > 1) {
  //     // Check if number of added days matches the date range
  //     if (addedDays === dateRange) return false
  //     return true
  //   }
  // }

  return (
    <Modal
      visible={visible}
      width={width}
      title={title}
      okText="Submit"
      cancelText="Cancel"
      confirmLoading={loading}
      destroyOnClose
      okButtonProps={{
        // disabled: isSubmitDisabled(),
        key: forceUpdate
      }}
      onCancel={() => {
        form.resetFields()
        setSelectedActivity('')
        setDaySections([])
        setShowAddDateButton(false)
        setAddedDays(0)
        setCanAddMore(false)
        setForceUpdate(0)
        handleCancel()
      }}
      onOk={() => {
        console.log(isFormValid())
        form
          .validateFields()
          .then((values) => {
            if (selectedType === 3 && !isFormValid()) {
              showMessage(ALERT_TYPES.ERROR, 'Please fill all required fields for the tour planning')
              return
            }
            handleSubmit(values)
          })
          .catch((info) => {
            console.error('Validate Failed:', info)
            showMessage(ALERT_TYPES.ERROR, 'Please fill all required fields')
          })
      }}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          onValuesChange={onValuesChange}
          onFieldsChange={() => {
            const type = form.getFieldValue('type_id')
            setSelectedType(type)
          }}
          name={formName}
        >
          <Form.Item
            {...FORM_ITEM_LAYOUT}
            name="type_id"
            label="Type"
            rules={[
              {
                required: true,
                message: 'Select type',
              },
            ]}
          >
            <Radio.Group
              options={allfieldsData?.type?.map((i) => ({ value: i.id, label: i.name }))}
            />
          </Form.Item>

          {/* Original fields for other types */}
          {selectedType !== 3 && (
            <>
              {selectedType === 1 && (
                <Form.Item
                  {...FORM_ITEM_LAYOUT}
                  name="from_date"
                  label="Date"
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    className="gx-w-100"
                    placeholder="Select date"
                    format={DATE_FORMAT}
                  />
                </Form.Item>
              )}
              <Form.Item
                {...FORM_ITEM_LAYOUT}
                name="from"
                label="From"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                {selectedType === 1 ? (
                  <TimePicker
                    className="gx-w-100"
                    placeholder="From time"
                    format={TIME_FORMAT}
                  />
                ) : (
                  <DatePicker
                    className="gx-w-100"
                    placeholder="From date"
                    format={DATE_FORMAT}
                  />
                )}
              </Form.Item>
              <Form.Item
                {...FORM_ITEM_LAYOUT}
                name="to"
                label="To"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                {selectedType === 1 ? (
                  <TimePicker
                    className="gx-w-100"
                    placeholder="To time"
                    format={TIME_FORMAT}
                  />
                ) : (
                  <DatePicker
                    className="gx-w-100"
                    placeholder="To date"
                    format={DATE_FORMAT}
                  />
                )}
              </Form.Item>
              <Form.Item
                {...FORM_ITEM_LAYOUT}
                name="description"
                label="Description"
              >
                <Input.TextArea allowClear />
              </Form.Item>
            </>
          )}
          {/* Conditional fields for type_id 3 */}
          {selectedType === 3 && (
            <>
              <Row gutter={24} justify="end" style={{ marginRight: '8px', gap: '32px' }}>
                <Col span={8}>
                  <Form.Item
                    name="from_date"
                    label="From"
                    rules={[{ required: true, message: 'Please select from date' }]}
                  >
                    <DatePicker
                      className="gx-w-100"
                      placeholder="From date"
                      format={DATE_FORMAT}
                      onChange={(date) => handleFromDateChange(date)}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="to_date"
                    label="To"
                    rules={[{ required: true, message: 'Please select to date' }]}
                  >
                    <DatePicker
                      className="gx-w-100"
                      placeholder="To date"
                      format={DATE_FORMAT}
                      onChange={(date) => handleToDateChange(date)}
                    />
                  </Form.Item>
                </Col>
              </Row>


              <Row gutter={24} justify="end" style={{ marginRight: '8px', gap: '32px' }}>
                <Col span={17}>
                  <Form.Item
                    name="tentative_cost"
                    label="Tentative Cost"
                  >
                    <InputNumber className="gx-w-100" placeholder="Tentative Cost" allowClear type="number" />
                  </Form.Item>
                </Col>
                <Col span={17}>
                  <Form.Item
                    name="tour_purpose_id"
                    label="Purpose"
                    rules={[{ required: true, message: 'Please select a purpose' }]}
                  >
                    <Select
                      className="gx-w-100"
                      placeholder="Select Purpose"
                      options={allfieldsData?.purpose?.map((i) => ({ value: i.id, label: i.name }))}
                      onChange={(value, option) => {
                        setSelectedActivity(option?.label)
                        // Clear description field when purpose changes
                        if (option?.label !== 'Other') {
                          form.setFieldsValue({ description: undefined })
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {selectedActivity === 'Other' && (
                <Row gutter={24} justify="end" style={{ marginRight: '8px', gap: '32px' }}>
                  <Col span={17}>
                    <Form.Item
                      name="description"
                      label="Description"
                      rules={[
                        {
                          required: true,
                          message: 'Please provide description for Others',
                          validator: (_, value) => {
                            if (selectedActivity === 'Other' && (!value || value.trim() === '')) {
                              return Promise.reject(new Error('Please provide description for Others'));
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <Input.TextArea
                        allowClear
                        placeholder="Please describe the activity"
                        rows={1}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {/* Day sections */}
              {daySections.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <Row justify="space-between" align="middle" style={{ marginLeft: 0, marginRight: 0, marginBottom: '16px', borderBottom: '1px solid #d9d9d9' }}>
                    <Col>
                      <h4 style={{ marginLeft: 10, color: '#1890ff' }}>Tour Planning</h4>
                    </Col>
                    {/* <Col>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={addNewDay}
                        size="small"
                        disabled={!canAddMore}
                        style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                      >
                        Add Plan
                      </Button>
                    </Col> */}
                  </Row>

                  {/* Table-like structure */}
                  <div style={{
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    overflow: 'hidden',
                  }}
                  >
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      backgroundColor: '#f5f5f5',
                      padding: '12px 16px',
                      borderBottom: '1px solid #d9d9d9',
                      fontWeight: 'bold',
                      fontSize: '12px',
                      color: '#666',
                      textTransform: 'uppercase'
                    }}>
                      <div style={{ flex: '0 0 16%', paddingRight: '12px', paddingLeft: '8px' }}>DATE</div>
                      <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>LOCATION</div>
                      <div style={{ flex: '0 0 21%', paddingRight: '12px', paddingLeft: '8px' }}>PLANNED ACTIVITY</div>
                      <div style={{ flex: '0 0 16%', paddingRight: '12px', paddingLeft: '16px' }}>MODE OF TRANSPORT</div>
                      <div style={{ flex: '0 0 25%', paddingRight: '12px', paddingLeft: '16px' }}>DESCRIPTION</div>
                      {/* <div style={{ flex: '0 0 5%', textAlign: 'center' }}>ACTION</div> */}
                    </div>

                    {/* Rows */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {daySections.map((day, index) => (
                        <div
                          key={day.key}
                          style={{
                            display: 'flex',
                            gap: '12px',
                            padding: '16px 16px',
                            borderBottom: index < addedDays - 1 ? '1px solid #f0f0f0' : 'none',
                            alignItems: 'center',
                            backgroundColor: '#fff'
                          }}
                        >
                          {/* Date - Read Only */}
                          <div style={{ flex: '0 0 15%', paddingRight: '12px', paddingLeft: '8px' }}>
                            <Form.Item
                              name={`${day.key}_date`}
                              style={{ marginBottom: '0px' }}
                              initialValue={day.date}
                            >
                              <Input
                                value={day.date}
                                readOnly
                                size="middle"
                                style={{
                                  borderRadius: '4px',
                                  height: '36px',
                                  backgroundColor: '#f5f5f5',
                                  color: '#666',
                                  cursor: 'not-allowed'
                                }}
                              />
                            </Form.Item>
                          </div>

                          {/* Location */}
                          <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>
                            <Form.Item
                              name={`${day.key}_location`}
                              style={{ marginBottom: '0px' }}
                              rules={[{ required: true, message: 'Please enter location' }]}
                            >
                              <Input
                                placeholder="Location"
                                size="middle"
                                style={{ borderRadius: '4px', height: '36px' }}
                              />
                            </Form.Item>
                          </div>

                          {/* Planned Activity - Multi Select */}
                          <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>
                            <Form.Item
                              name={`${day.key}_planned_activity`}
                              style={{ marginBottom: '0px' }}
                              rules={[{ required: true, message: 'Please select planned activity' }]}
                            >
                              <Select
                                mode="multiple"
                                placeholder="Planned Activity"
                                allowClear
                                size="middle"
                                style={{ borderRadius: '4px', minHeight: '36px' }}
                                options={allfieldsData?.planned_activity?.map((i) => ({ value: i.id, label: i.name }))}
                              />
                            </Form.Item>
                          </div>

                          {/* Mode of Transport */}
                          <div style={{ flex: '0 0 15%', paddingRight: '12px', paddingLeft: '8px' }}>
                            <Form.Item
                              name={`${day.key}_transport`}
                              style={{ marginBottom: '0px' }}
                              rules={[{ required: true, message: 'Please select mode of transport' }]}
                            >
                              <Select
                                placeholder="Mode of Transport"
                                allowClear
                                size="middle"
                                style={{ borderRadius: '4px', height: '36px' }}
                                options={allfieldsData?.transport?.map((i) => ({ value: i.id, label: i.name }))}
                              />
                            </Form.Item>
                          </div>

                          {/* Description */}
                          <div style={{ flex: '0 0 25%', paddingRight: '12px', paddingLeft: '8px' }}>
                            <Form.Item
                              name={`${day.key}_description`}
                              style={{ marginBottom: '0px' }}
                              rules={[{ required: true, message: 'Please enter description' }]}
                            >
                              <Input.TextArea
                                placeholder="Description"
                                rows={1}
                                size="middle"
                                style={{ resize: 'none', borderRadius: '4px', minHeight: '36px' }}
                              />
                            </Form.Item>
                          </div>

                          {/* Action */}
                          {/* <div style={{ flex: '0 0 5%', textAlign: 'center' }}>
                            <Button
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={() => removeDay(index)}
                              size="small"
                              style={{
                                color: '#ff4d4f',
                                backgroundColor: '#fff2f0',
                                border: '1px solid #ffccc7',
                                borderRadius: '4px'
                              }}
                            />
                          </div> */}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  )
}

FormModal.propTypes = {
  visible: PropTypes.bool,
  formName: PropTypes.string,
  width: PropTypes.number,
  title: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  types: PropTypes.array,
  handleCancel: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  handleOk: PropTypes.func.isRequired,
}

FormModal.defaultProps = {
  formName: 'dynamic_rule',
  title: 'form title',
  types: [],
  visible: false,
  width: 1200,
}

export default FormModal
